import React, { useMemo, useState, useEffect } from "react";
import { Link, useParams, useLocation, useHistory } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  Badge,
  Card,
  CardBody,
  CardHeader,
} from "reactstrap";
import { FaChevronRight } from "react-icons/fa";
import cogoToast from "cogo-toast";
import { genericPost } from "../../services/Api";
import { ENDPOINTS, SUCCESS_RESULT } from "../../Constants";
import ability from "../config/defineAbility";
import { PERMS } from "../config/perms";
import "./ProjectDetails.css";

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch (e) {
    return value;
  }
};

const ActivityRegistrationProgress = ({ registered, target }) => {
  const reg = Number(registered) || 0;
  const tgt = Number(target) || 0;
  const hasTarget = tgt > 0;
  const pct = hasTarget ? Math.min(100, (reg / tgt) * 100) : 0;
  const pctRounded = Math.round(pct);
  const isComplete = hasTarget && reg >= tgt;
  const barColor = isComplete
    ? "linear-gradient(90deg, #22c55e 0%, #16a34a 100%)"
    : pctRounded >= 75
    ? "linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)"
    : pctRounded >= 40
    ? "linear-gradient(90deg, #667eea 0%, #4f46e5 100%)"
    : "linear-gradient(90deg, #818cf8 0%, #6366f1 100%)";

  return (
    <div className="activity-reg-progress">
      <div
        className="d-flex justify-content-between align-items-center"
        style={{ marginBottom: 6, fontSize: "0.72rem" }}
      >
        <span style={{ color: "#6c757d", fontWeight: 600, letterSpacing: "0.02em" }}>
          Registration progress
        </span>
        <span
          style={{
            fontWeight: 800,
            color: isComplete ? "#16a34a" : "#2563eb",
            fontSize: "0.8rem",
          }}
        >
          {hasTarget ? `${pctRounded}%` : "—"}
        </span>
      </div>
      <div
        style={{
          height: 12,
          borderRadius: 999,
          backgroundColor: "#e9ecef",
          overflow: "hidden",
          boxShadow: "inset 0 1px 2px rgba(0,0,0,0.06)",
        }}
      >
        <div
          style={{
            width: hasTarget ? `${pct}%` : "0%",
            height: "100%",
            borderRadius: 999,
            background: barColor,
            transition: "width 0.45s ease",
            boxShadow: pct > 0 ? "0 1px 3px rgba(37, 99, 235, 0.35)" : "none",
          }}
        />
      </div>
      <div
        className="d-flex justify-content-between"
        style={{ marginTop: 6, fontSize: "0.7rem", color: "#6c757d" }}
      >
        <span>
          <strong style={{ color: "#0d6efd" }}>{reg.toLocaleString()}</strong>
          {hasTarget ? (
            <>
              {" "}
              / {tgt.toLocaleString()} registered
            </>
          ) : (
            " registered"
          )}
        </span>
        {hasTarget && (
          <span style={{ fontWeight: 600, color: isComplete ? "#16a34a" : "#64748b" }}>
            {isComplete ? "Target reached" : `${Math.max(0, tgt - reg).toLocaleString()} remaining`}
          </span>
        )}
      </div>
    </div>
  );
};

const ProjectDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const history = useHistory();
  const passedProject = location?.state?.project || null;
  const [selectedComponentType, setSelectedComponentType] = useState("ALL");
  const [activitySearch, setActivitySearch] = useState("");
  const [projectDetailsData, setProjectDetailsData] = useState({ project: {}, activities: [] });
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [apiError, setApiError] = useState("");

  useEffect(() => {
    const projectId = id || passedProject?.id;
    if (!projectId) return;

    setIsLoadingDetails(true);
    setApiError("");
    genericPost(ENDPOINTS.PROJECT_DETAILS, { project_id: projectId })
      .then((res) => {
        setIsLoadingDetails(false);
        if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
          const payload = res?.data?.data || {};
          setProjectDetailsData(payload);
        } else {
          const msg = res?.data?.message || "Failed to load project details";
          setProjectDetailsData({ project: passedProject || {}, activities: [] });
          setApiError(msg);
          cogoToast.error(msg, { hideAfter: 4 });
        }
      })
      .catch((e) => {
        setIsLoadingDetails(false);
        console.error("Project details load error", e);
        setProjectDetailsData({ project: passedProject || {}, activities: [] });
        setApiError("Failed to load project details");
        cogoToast.error("Failed to load project details", { hideAfter: 4 });
      });
  }, [id, passedProject?.id]);

  const pageData = useMemo(() => {
    const projectFromApi = projectDetailsData?.project || {};
    const project = {
      ...projectFromApi,
      ...(passedProject || {}),
      id: id || projectFromApi.id,
    };

    return {
      project,
      activities: projectDetailsData?.activities || [],
    };
  }, [passedProject, id, projectDetailsData]);

  const { project, activities } = pageData;
  const canManageComponents = ability.can(PERMS.READ, PERMS.MANAGE_COMPONENTS);
  const totalRegistered = activities.reduce((sum, x) => sum + (Number(x.total_registered) || 0), 0);
  const componentTypeOptions = useMemo(() => {
    const uniq = Array.from(
      new Set((activities || []).map((a) => a?.component_type_name).filter(Boolean))
    );
    return uniq;
  }, [activities]);

  const filteredActivities = useMemo(() => {
    const base =
      selectedComponentType === "ALL"
        ? activities || []
        : (activities || []).filter((a) => a?.component_type_name === selectedComponentType);

    const q = (activitySearch || "").trim().toLowerCase();
    if (!q) return base;

    return base.filter((a) => {
      const cycleNames = (a?.active_cycles || []).map((c) => c?.name || "").join(" ").toLowerCase();
      return (
        (a?.component_name || "").toLowerCase().includes(q) ||
        (a?.component_id || "").toLowerCase().includes(q) ||
        (a?.component_type_name || "").toLowerCase().includes(q) ||
        cycleNames.includes(q)
      );
    });
  }, [activities, selectedComponentType, activitySearch]);

  return (
    <div style={{ padding: "20px", minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      <Breadcrumb className="ms-1 mb-3">
        <BreadcrumbItem>
          <Link to="/dashboard">Dashboard</Link>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <Link to="/projects/list">Projects</Link>
        </BreadcrumbItem>
        <BreadcrumbItem active>Project Details</BreadcrumbItem>
      </Breadcrumb>
      {isLoadingDetails && (
        <div className="text-muted mb-2" style={{ fontSize: "0.85rem" }}>
          Loading latest project details...
        </div>
      )}
      {!!apiError && !isLoadingDetails && (
        <div className="alert alert-warning py-2 mb-2" role="alert">
          {apiError}
        </div>
      )}

      <div className="row g-3 mb-3">
        <div className="col-lg-8">
          <Card
            className="border-0 shadow-sm h-100"
            style={{
              borderRadius: "22px",
              overflow: "hidden",
              background: "linear-gradient(135deg, #6477dd 0%, #7a4aa5 100%)",
              color: "white",
              position: "relative",
              minHeight: "250px",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: -18,
                right: -18,
                width: 120,
                height: 120,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.10)",
              }}
            />
            <div
              style={{
                position: "absolute",
                left: -30,
                bottom: -35,
                width: 140,
                height: 140,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.08)",
              }}
            />

            <CardBody style={{ padding: "28px 30px", position: "relative", zIndex: 1 }}>
               
              <div style={{ fontSize: "2rem", fontWeight: 700, lineHeight: 1.2, marginBottom: "10px" }}>
                {project?.project_name || "Project Details"}
              </div>
              <div style={{ fontSize: "1.15rem", fontWeight: 600, opacity: 0.95, marginBottom: "10px" }}>
                {project?.project_code || "-"} {project?.alias ? `• ${project.alias}` : ""}
              </div>
              <div style={{ fontSize: "1rem", opacity: 0.9, maxWidth: "95%" }}>
                {project?.project_description || "-"}
              </div>

              
            </CardBody>
          </Card>
        </div>
<br/>
        <div className="col-lg-4">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody
                style={{
                  border: "1.5px solid #dbe4ff",
                  borderRadius: "12px",
                  background: "linear-gradient(180deg, #ffffff 0%, #f7f9ff 100%)",
                }}
              >
                <div className="text-muted" style={{ fontSize: "0.8rem", fontWeight: 600 }}>Activities</div>
                <div style={{ fontSize: "1.35rem", fontWeight: 800, color: "#1f2a44" }}>{activities.length}</div>
              </CardBody>
            </Card>
            <Card className="border-0 shadow-sm h-100">
              <CardBody
                style={{
                  border: "1.5px solid #cfe5ff",
                  borderRadius: "12px",
                  background: "linear-gradient(180deg, #ffffff 0%, #f2f8ff 100%)",
                }}
              >
                <div className="text-muted" style={{ fontSize: "0.8rem", fontWeight: 600 }}>Total Registered</div>
                <div style={{ fontSize: "1.35rem", fontWeight: 800, color: "#0d6efd" }}>{totalRegistered.toLocaleString()}</div>
              </CardBody>
            </Card>
            <Card className="border-0 shadow-sm h-100">
              <CardBody
                style={{
                  border: "1.5px solid #d8f3e5",
                  borderRadius: "12px",
                  background: "linear-gradient(180deg, #ffffff 0%, #f4fdf8 100%)",
                }}
              >
                <div className="text-muted" style={{ fontSize: "0.8rem", fontWeight: 600 }}>Start</div>
                <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "#1f7a4a" }}>{formatDate(project?.startdate)}</div>
              </CardBody>
            </Card>
            <Card className="border-0 shadow-sm h-100">
              <CardBody
                style={{
                  border: "1.5px solid #ffe4d6",
                  borderRadius: "12px",
                  background: "linear-gradient(180deg, #ffffff 0%, #fff7f2 100%)",
                }}
              >
                <div className="text-muted" style={{ fontSize: "0.8rem", fontWeight: 600 }}>End</div>
                <div style={{ fontSize: "1.05rem", fontWeight: 800, color: "#b45309" }}>{formatDate(project?.enddate)}</div>
              </CardBody>
            </Card>
          </div>
          <div className="mt-2 d-flex justify-content-end gap-2 flex-wrap">
            <Link
              to={{
                pathname: "/projects/view",
                state: project?.id,
              }}
              className="btn btn-outline-primary btn-sm"
              style={{ fontWeight: 600 }}
            >
              Go to Master Record
            </Link>
            {canManageComponents && (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                style={{ fontWeight: 600 }}
                disabled={!project?.id}
                onClick={() =>
                  history.push({
                    pathname: "/projects/components",
                    state: { project: { project_id: project?.id } },
                  })
                }
              >
                Project Components
              </button>
            )}
          </div>
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader
          style={{ fontWeight: 700 }}
          className="d-flex justify-content-between align-items-center gap-3"
        >
          <div style={{ minWidth: 180 }}>
            <span>Project Activities</span>
          </div>
          <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <input
              type="text"
              className="form-control form-control-sm"
              value={activitySearch}
              onChange={(e) => setActivitySearch(e.target.value)}
              placeholder="Search activity, ID, type, cycle..."
              style={{ width: "100%", maxWidth: 460, minWidth: 240 }}
            />
          </div>
          <div style={{ minWidth: 240, maxWidth: 280 }}>
            <select
              className="form-control form-control-sm"
              value={selectedComponentType}
              onChange={(e) => setSelectedComponentType(e.target.value)}
            >
              <option value="ALL">All Component Types</option>
              {componentTypeOptions.map((typeName) => (
                <option key={typeName} value={typeName}>
                  {typeName}
                </option>
              ))}
            </select>
          </div>
        </CardHeader>
        <CardBody style={{ padding: "12px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {isLoadingDetails && (
              <>
                {[1, 2, 3].map((s) => (
                  <div key={`activity-loading-${s}`} className="activity-card" style={{ opacity: 0.8, cursor: "default" }}>
                    <div className="activity-card-layout">
                      <div className="activity-card-main">
                        <div style={{ width: 36, height: 36, borderRadius: 8, background: "#e9ecef" }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ height: 14, width: "85%", maxWidth: 320, background: "#e9ecef", borderRadius: 6, marginBottom: 8 }} />
                          <div style={{ height: 10, width: "55%", maxWidth: 200, background: "#f1f3f5", borderRadius: 6 }} />
                        </div>
                      </div>
                      <div className="activity-reg-progress-wrap">
                        <div style={{ height: 8, width: "100%", background: "#e9ecef", borderRadius: 999, marginBottom: 6 }} />
                        <div style={{ height: 8, width: "55%", background: "#dee2e6", borderRadius: 999 }} />
                      </div>
                      <div className="d-none d-md-block" style={{ width: 80, height: 26, background: "#f1f3f5", borderRadius: 8 }} />
                    </div>
                  </div>
                ))}
                <div className="text-muted text-center py-2" style={{ fontSize: "0.85rem" }}>
                  Loading activities...
                </div>
              </>
            )}
            {!isLoadingDetails && filteredActivities.map((row, index) => (
              <div
                key={row.component_id || index}
                className="activity-card"
                onClick={() =>
                  history.push({
                    pathname: `/projects/activity-beneficiaries/${row?.component_id}`,
                    state: {
                      project_id: project?.id,
                      project_name: project?.alias || project?.project_name || "",
                      component_name: row?.component_name,
                      total_registered: Number(row?.total_registered) || 0,
                    },
                  })
                }
              >
                <div className="activity-card-layout">
                  <div className="activity-card-main">
                    <div className="activity-index">{index + 1}</div>
                    <div className="activity-card-text">
                      <div className="activity-card-name">{row.component_name || "-"}</div>
                      <div className="activity-card-badges">
                        <Badge color="info" pill style={{ fontSize: "0.72rem", fontWeight: 600, padding: "6px 10px" }}>
                          {row.component_type_name || "-"}
                        </Badge>
                        <Badge color="secondary" pill style={{ fontSize: "0.72rem", fontWeight: 600, padding: "6px 10px" }}>
                          Cycles: {(row.active_cycles || []).length}
                        </Badge>
                      </div>
                    </div>
                    <div className="activity-chevron d-md-none">
                      <FaChevronRight />
                    </div>
                  </div>
                  <div className="activity-reg-progress-wrap">
                    <ActivityRegistrationProgress
                      registered={row.total_registered}
                      target={row.total_target}
                    />
                  </div>
                  <div className="activity-registered-stat d-none d-md-block">
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>Registered</div>
                    <div style={{ fontWeight: 700, color: "#0d6efd" }}>{Number(row.total_registered || 0).toLocaleString()}</div>
                  </div>
                  <div className="activity-chevron d-none d-md-flex">
                    <FaChevronRight />
                  </div>
                </div>
              </div>
            ))}
            {!isLoadingDetails && filteredActivities.length === 0 && (
              <div className="text-muted text-center py-3">No activities found for selected component type.</div>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default ProjectDetails;
